// react
import { useState } from 'react';

// components
import { Accordion, Item } from '@/components/atoms/Accordion/Accordion';
import { Dialog } from '@/components/atoms/Dialog/Dialog';
import { Icon } from '@/components/atoms/Icon/Icon';
import type { Icons } from '@/components/atoms/Icon/icons';
import { resolveIcon } from '@/components/atoms/Icon/resolveIcon';

// theme
import { theme } from '@/theme';

// models
import { Section } from '@karehero/models';

// mui
import { Box, Stack, Typography } from '@mui/material';

const colorAndIconMap = {
  complete: {
    primary: theme.color.success[10],
    secondary: theme.color.success[40],
    icon: 'check',
  },
  inProgress: {
    primary: theme.color.warning[10],
    secondary: theme.color.warning[40],
    icon: 'minus',
  },
  notStarted: {
    primary: theme.color.neutral[10],
    secondary: theme.color.neutral[40],
    icon: 'check',
  },
};

export interface SectionsAccordionProps {
  sections?: Section[];
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  currentPath?: string;
  onNavigate?: (to: string) => void;
}

/**
 * SectionsAccordion displays a right-side sheet dialog with expandable sections
 * showing form completion status. Each section contains subsections that can be
 * clicked to navigate to the corresponding form.
 */
export const SectionsAccordion = ({
  sections,
  isOpen,
  setIsOpen,
  currentPath,
  onNavigate,
}: SectionsAccordionProps) => {
  return (
    <Dialog
      variant='sheet'
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      direction='right'
    >
      <Typography
        variant='subtitle1'
        sx={{
          fontWeight: 600,
          mb: 2,
        }}
      >
        Jump to a section
      </Typography>
      <Stack spacing={2} sx={{ overflowY: 'auto', maxHeight: '100%' }}>
        <Accordion
          items={sections?.map((section: Section) => {
            const status = colorAndIconMap[section.sectionStatus];

            const item: Item = {
              id: section.id,
              label: section.title,
              content: (
                <>
                  {section.subSections?.map((subsec) => (
                    <Box
                      component='button'
                      key={subsec.id}
                      onClick={() =>
                        subsec.to && onNavigate && onNavigate(subsec.to)
                      }
                      sx={{
                        all: 'unset',
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                        borderRadius: '4px',
                        ml: 2,
                        pl: 2,
                        transitionDuration: '0.2s',
                        bgcolor:
                          currentPath === subsec.to
                            ? 'neutral.30'
                            : 'transparent',
                        '&:hover': {
                          bgcolor: 'neutral.30',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          p: 0.5,
                          borderRadius: '99999px',
                          width: 24,
                          minWidth: 24,
                          maxWidth: 24,
                          height: 24,
                          minHeight: 24,
                          maxHeight: 24,
                          bgcolor:
                            colorAndIconMap[subsec.sectionStatus].secondary,
                          color: 'white',
                          mr: 1,
                        }}
                      >
                        <Icon
                          icon={
                            colorAndIconMap[subsec.sectionStatus].icon as Icons
                          }
                        />
                      </Box>
                      <Typography variant='body2' sx={{ py: 1.5 }}>
                        {subsec.title}
                      </Typography>
                    </Box>
                  ))}
                </>
              ),
              icon: resolveIcon(status.icon),
              primaryColor: status.primary,
              secondaryColor: status.secondary,
            };
            return item;
          })}
        />
      </Stack>
    </Dialog>
  );
};

SectionsAccordion.displayName = 'SectionsAccordion';

export default SectionsAccordion;
